import { parseFloatFromString, parseReadableNumberToFloat } from './formatters';

/**
 * Get token details by ID
 * @param {string} tokenId - ID of the token to find
 * @param {Array} tokens - Array of token objects
 * @returns {Object|null} - Token object or null if not found
 */
export const getTokenById = (tokenId, tokens) => {
  return tokens.find(t => t.id === tokenId) || null;
};

/**
 * Calculate the total allocation amount
 * @param {Array} allocations - Array of allocation objects
 * @returns {number} - Total allocation amount
 */
export const calculateTotalAllocation = (allocations) => {
  return allocations.reduce((sum, item) => {
    return sum + parseFloatFromString(item.amount || 0);
  }, 0);
};

/**
 * Create allocation data with token details
 * @param {Array} allocations - Array of allocation objects
 * @param {Array} tokens - Array of token objects (optional, for backward compatibility)
 * @param {number} totalAllocation - Total allocation amount
 * @returns {Array} - Enhanced allocation data with token details
 */
export const createAllocationData = (allocations, tokens, totalAllocation) => {
  if (!allocations || !Array.isArray(allocations)) {
    return [];
  }
  
  return allocations
    .filter(item => item && (item.tokenId || item.coingeckoApiId || item.token)) // Accept items with any token identifier
    .map(item => {
      let token = item.token; // Use token object directly if available (from GraphQL)
      
      // Fallback to lookup for backward compatibility
      if (!token && item.tokenId) {
        token = getTokenById(item.tokenId, tokens);
      }
      
      // If still no token found, try to create from selectedTokenInfo (legacy support)
      if (!token && (item.tokenId || item.coingeckoApiId) && item.selectedTokenInfo) {
        const tokenInfo = item.selectedTokenInfo;
        token = {
          id: item.tokenId || `temp-${item.coingeckoApiId}`,
          name: tokenInfo.name || 'Selected Token',
          ticker: tokenInfo.ticker || 'N/A',
          coingeckoApiId: item.coingeckoApiId,
          group: null,
          location: null,
          marketData: null
        };
      }
      
      if (!token) return null; // Skip if no token data available at all
      
      const amount = parseFloatFromString(item.amount || 0);
      
      return {
        ...item,
        token,
        amount, // Ensure amount is a number
        percentage: totalAllocation > 0 ? (amount / totalAllocation) * 100 : 0,
        groupId: token?.group?.id,
        groupName: token?.group?.name,
        locationId: item.targetLocation?.id || item.targetLocationId,
        locationName: item.targetLocation?.name || item.targetLocationName,
      };
    })
    .filter(item => item && item.token); // Filter out null items and those with no token data
};

/**
 * Group allocations by category (group, location, market cap)
 * @param {Array} allocationData - Enhanced allocation data
 * @param {string} categoryKey - Key to group by ('groupId', 'locationId', etc.)
 * @param {string} categoryNameKey - Key for the category name
 * @param {number} totalAllocation - Total allocation amount
 * @returns {Object} - Grouped allocation data
 */
export const groupAllocationsByCategory = (allocationData, categoryKey, categoryNameKey, totalAllocation) => {
  const groupedAllocations = allocationData.reduce((groups, item) => {
    const categoryId = item[categoryKey] || 'unknown';
    const categoryName = item[categoryNameKey] || 'Unknown';
    
    if (!groups[categoryId]) {
      groups[categoryId] = { 
        id: categoryId,
        name: categoryName,
        amount: 0,
        completedAmount: 0,
        percentage: 0,
        items: []
      };
    }
    
    const amt = parseFloatFromString(item.amount);
    groups[categoryId].amount += amt;
    if (item.completed) {
      groups[categoryId].completedAmount += amt;
    }
    groups[categoryId].items.push(item);
    
    return groups;
  }, {});
  
  // Calculate percentages
  Object.values(groupedAllocations).forEach(category => {
    category.percentage = totalAllocation > 0 ? (category.amount / totalAllocation) * 100 : 0;
  });
  
  return groupedAllocations;
};

/**
 * Get market cap category from market cap value
 * @param {string} marketCapString - Market cap value as string
 * @returns {Object} - Object with category, color, and formatted value
 */
export const getMarketCapInfo = (marketCapString) => {
  const marketCap = parseReadableNumberToFloat(marketCapString || '0');
  let category = 'Unknown';
  let color = '#6B7280';
  
  if (marketCap >= 10_000_000_000) { // > $10B
    category = 'Large Cap';
    color = '#3B82F6';
  } else if (marketCap >= 1_000_000_000) { // > $1B
    category = 'Mid Cap';
    color = '#10B981';
  } else if (marketCap >= 100_000_000) { // > $100M
    category = 'Small Cap';
    color = '#F59E0B';
  } else if (marketCap >= 10_000_000) { // > $10M
    category = 'Micro Cap';
    color = '#EF4444';
  } else if (marketCap > 0) { // < $10M but > 0
    category = 'Nano Cap';
    color = '#8B5CF6';
  }
  
  return {
    category,
    color,
    formattedValue: formatMarketCap(marketCap)
  };
};

/**
 * Format market cap for display
 * @param {number} marketCap - Market cap value as number
 * @returns {string} - Formatted market cap string
 */
export const formatMarketCap = (marketCap) => {
  if (marketCap >= 1_000_000_000) {
    return `$${(marketCap / 1_000_000_000).toFixed(2)}B`;
  } else if (marketCap >= 1_000_000) {
    return `$${(marketCap / 1_000_000).toFixed(2)}M`;
  } else if (marketCap >= 1_000) {
    return `$${(marketCap / 1_000).toFixed(2)}K`;
  } else {
    return `$${marketCap.toLocaleString()}`;
  }
};



/**
 * Generate a random color for charts (helper for assigning colors to categories)
 * @param {number} index - Index of the color to retrieve
 * @returns {string} - Hex color code
 */
export const getRandomColor = (index) => {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green  
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#6366F1', // indigo
    '#14B8A6', // teal
    '#F97316', // orange
    '#06B6D4'  // cyan
  ];
  
  return colors[index % colors.length];
}; 